export interface ThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  border: string;
  success: string;
  warning: string;
  error: string;
  info: string;
  keypadBackground: string;
  keypadButton: string;
  keypadButtonText: string;
  backButton: string;
  backButtonText: string;
  modalBackground: string;
  modalOverlay: string;
  confirmButton: string;
  confirmButtonText: string;
}

export interface ThemeTemplate {
  id: string;
  name: string;
  description: string;
  colors: ThemeColors;
  borderRadius: 'none' | 'small' | 'medium' | 'large' | 'full';
  fontFamily: string;
  shadows: 'none' | 'subtle' | 'medium' | 'bold';
  animations: 'none' | 'subtle' | 'smooth' | 'energetic';
}

export const THEME_TEMPLATES: ThemeTemplate[] = [
  {
    id: 'default',
    name: 'Orange Classique (Défaut)',
    description: 'Palette orange originale de votre application',
    colors: {
      primary: '#F97316',
      secondary: '#FB923C',
      accent: '#FDBA74',
      background: '#FFFFFF',
      surface: '#F9FAFB',
      text: '#1F2937',
      textSecondary: '#6B7280',
      border: '#E5E7EB',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6',
      keypadBackground: '#FEF3E7',
      keypadButton: '#F97316',
      keypadButtonText: '#FFFFFF',
      backButton: '#9CA3AF',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(0, 0, 0, 0.5)',
      confirmButton: '#F97316',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'large',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'medium',
    animations: 'smooth',
  },
  {
    id: 'modern',
    name: 'Moderne',
    description: 'Design minimaliste et épuré',
    colors: {
      primary: '#0EA5E9',
      secondary: '#38BDF8',
      accent: '#7DD3FC',
      background: '#FFFFFF',
      surface: '#F8FAFC',
      text: '#0F172A',
      textSecondary: '#64748B',
      border: '#E2E8F0',
      success: '#22C55E',
      warning: '#EAB308',
      error: '#EF4444',
      info: '#06B6D4',
      keypadBackground: '#F8FAFC',
      keypadButton: '#0EA5E9',
      keypadButtonText: '#FFFFFF',
      backButton: '#64748B',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(15, 23, 42, 0.5)',
      confirmButton: '#22C55E',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'medium',
    fontFamily: 'Inter, system-ui, sans-serif',
    shadows: 'subtle',
    animations: 'subtle',
  },
  {
    id: 'vibrant',
    name: 'Vibrant',
    description: 'Couleurs vives et énergiques',
    colors: {
      primary: '#EC4899',
      secondary: '#F472B6',
      accent: '#FBBF24',
      background: '#FAFAFA',
      surface: '#FFFFFF',
      text: '#18181B',
      textSecondary: '#71717A',
      border: '#E4E4E7',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#F43F5E',
      info: '#8B5CF6',
      keypadBackground: '#FFFFFF',
      keypadButton: '#EC4899',
      keypadButtonText: '#FFFFFF',
      backButton: '#71717A',
      backButtonText: '#FFFFFF',
      modalBackground: '#FAFAFA',
      modalOverlay: 'rgba(24, 24, 27, 0.6)',
      confirmButton: '#10B981',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'full',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'bold',
    animations: 'energetic',
  },
  {
    id: 'dark',
    name: 'Sombre',
    description: 'Thème sombre élégant',
    colors: {
      primary: '#F97316',
      secondary: '#FB923C',
      accent: '#FDBA74',
      background: '#0F172A',
      surface: '#1E293B',
      text: '#F8FAFC',
      textSecondary: '#94A3B8',
      border: '#334155',
      success: '#22C55E',
      warning: '#FBBF24',
      error: '#F87171',
      info: '#60A5FA',
      keypadBackground: '#1E293B',
      keypadButton: '#F97316',
      keypadButtonText: '#FFFFFF',
      backButton: '#475569',
      backButtonText: '#F8FAFC',
      modalBackground: '#1E293B',
      modalOverlay: 'rgba(0, 0, 0, 0.8)',
      confirmButton: '#22C55E',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'large',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'bold',
    animations: 'smooth',
  },
  {
    id: 'elegant',
    name: 'Élégant',
    description: 'Sophistiqué et raffiné',
    colors: {
      primary: '#7C3AED',
      secondary: '#A78BFA',
      accent: '#C4B5FD',
      background: '#FFFFFF',
      surface: '#FAFAFA',
      text: '#18181B',
      textSecondary: '#52525B',
      border: '#E4E4E7',
      success: '#059669',
      warning: '#D97706',
      error: '#DC2626',
      info: '#2563EB',
      keypadBackground: '#FAFAFA',
      keypadButton: '#7C3AED',
      keypadButtonText: '#FFFFFF',
      backButton: '#52525B',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(24, 24, 27, 0.5)',
      confirmButton: '#059669',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'medium',
    fontFamily: 'Georgia, serif',
    shadows: 'subtle',
    animations: 'subtle',
  },
  {
    id: 'fresh',
    name: 'Frais',
    description: 'Thème vert et naturel',
    colors: {
      primary: '#10B981',
      secondary: '#34D399',
      accent: '#6EE7B7',
      background: '#FFFFFF',
      surface: '#F0FDF4',
      text: '#064E3B',
      textSecondary: '#047857',
      border: '#D1FAE5',
      success: '#059669',
      warning: '#F59E0B',
      error: '#DC2626',
      info: '#0891B2',
      keypadBackground: '#F0FDF4',
      keypadButton: '#10B981',
      keypadButtonText: '#FFFFFF',
      backButton: '#047857',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(6, 78, 59, 0.5)',
      confirmButton: '#059669',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'large',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'medium',
    animations: 'smooth',
  },
  {
    id: 'ocean',
    name: 'Océan',
    description: 'Profondeur marine et fraîcheur',
    colors: {
      primary: '#0891B2',
      secondary: '#06B6D4',
      accent: '#22D3EE',
      background: '#FFFFFF',
      surface: '#F0FDFA',
      text: '#134E4A',
      textSecondary: '#0F766E',
      border: '#CCFBF1',
      success: '#14B8A6',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6',
      keypadBackground: '#F0FDFA',
      keypadButton: '#0891B2',
      keypadButtonText: '#FFFFFF',
      backButton: '#0F766E',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(19, 78, 74, 0.5)',
      confirmButton: '#14B8A6',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'large',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'medium',
    animations: 'smooth',
  },
  {
    id: 'sunset',
    name: 'Coucher de soleil',
    description: 'Chaleur et énergie du crépuscule',
    colors: {
      primary: '#DC2626',
      secondary: '#F97316',
      accent: '#FBBF24',
      background: '#FFFFFF',
      surface: '#FEF2F2',
      text: '#7F1D1D',
      textSecondary: '#991B1B',
      border: '#FEE2E2',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#DC2626',
      info: '#3B82F6',
      keypadBackground: '#FEF2F2',
      keypadButton: '#DC2626',
      keypadButtonText: '#FFFFFF',
      backButton: '#991B1B',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(127, 29, 29, 0.5)',
      confirmButton: '#10B981',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'large',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'medium',
    animations: 'smooth',
  },
  {
    id: 'lavender',
    name: 'Lavande',
    description: 'Douceur et élégance florale',
    colors: {
      primary: '#A855F7',
      secondary: '#C084FC',
      accent: '#E9D5FF',
      background: '#FFFFFF',
      surface: '#FAF5FF',
      text: '#581C87',
      textSecondary: '#7E22CE',
      border: '#F3E8FF',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#8B5CF6',
      keypadBackground: '#FAF5FF',
      keypadButton: '#A855F7',
      keypadButtonText: '#FFFFFF',
      backButton: '#7E22CE',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(88, 28, 135, 0.5)',
      confirmButton: '#10B981',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'large',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'medium',
    animations: 'smooth',
  },
  {
    id: 'forest',
    name: 'Forêt',
    description: 'Nature profonde et apaisante',
    colors: {
      primary: '#15803D',
      secondary: '#16A34A',
      accent: '#4ADE80',
      background: '#FFFFFF',
      surface: '#F0FDF4',
      text: '#14532D',
      textSecondary: '#166534',
      border: '#DCFCE7',
      success: '#22C55E',
      warning: '#F59E0B',
      error: '#DC2626',
      info: '#3B82F6',
      keypadBackground: '#F0FDF4',
      keypadButton: '#15803D',
      keypadButtonText: '#FFFFFF',
      backButton: '#166534',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(20, 83, 45, 0.5)',
      confirmButton: '#22C55E',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'large',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'medium',
    animations: 'smooth',
  },
  {
    id: 'midnight',
    name: 'Minuit',
    description: 'Élégance nocturne moderne',
    colors: {
      primary: '#6366F1',
      secondary: '#818CF8',
      accent: '#A5B4FC',
      background: '#1E1B4B',
      surface: '#312E81',
      text: '#E0E7FF',
      textSecondary: '#A5B4FC',
      border: '#4C1D95',
      success: '#34D399',
      warning: '#FBBF24',
      error: '#F87171',
      info: '#60A5FA',
      keypadBackground: '#312E81',
      keypadButton: '#6366F1',
      keypadButtonText: '#FFFFFF',
      backButton: '#4C1D95',
      backButtonText: '#E0E7FF',
      modalBackground: '#312E81',
      modalOverlay: 'rgba(0, 0, 0, 0.8)',
      confirmButton: '#34D399',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'large',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'bold',
    animations: 'smooth',
  },
  {
    id: 'fastfood',
    name: 'Fast Food Moderne',
    description: 'Palette lisible et dynamique pour commander vite',
    colors: {
      primary: '#E63946',
      secondary: '#C62828',
      accent: '#FF9F1C',
      background: '#FFFFFF',
      surface: '#F2F2F2',
      text: '#1F1F1F',
      textSecondary: '#4A4A4A',
      border: '#E0E0E0',
      success: '#2E7D32',
      warning: '#FF9F1C',
      error: '#E63946',
      info: '#0D6EFD',
      keypadBackground: '#FFFFFF',
      keypadButton: '#E63946',
      keypadButtonText: '#FFFFFF',
      backButton: '#6B7280',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(31, 31, 31, 0.5)',
      confirmButton: '#2E7D32',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'medium',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'medium',
    animations: 'energetic',
  },
  {
    id: 'premium-dark',
    name: 'Restaurant Premium',
    description: 'Classe et moderne, très professionnel',
    colors: {
      primary: '#D4AF37',
      secondary: '#B8962E',
      accent: '#E8C468',
      background: '#121212',
      surface: '#1E1E1E',
      text: '#FFFFFF',
      textSecondary: '#B0B0B0',
      border: '#2A2A2A',
      success: '#4CAF50',
      warning: '#FF9800',
      error: '#F44336',
      info: '#2196F3',
      keypadBackground: '#1E1E1E',
      keypadButton: '#D4AF37',
      keypadButtonText: '#121212',
      backButton: '#3A3A3A',
      backButtonText: '#FFFFFF',
      modalBackground: '#1E1E1E',
      modalOverlay: 'rgba(0, 0, 0, 0.85)',
      confirmButton: '#D4AF37',
      confirmButtonText: '#121212',
    },
    borderRadius: 'small',
    fontFamily: 'Georgia, serif',
    shadows: 'subtle',
    animations: 'subtle',
  },
  {
    id: 'asian-sushi',
    name: 'Asiatique / Sushi',
    description: 'Élégante, propre et appétissante',
    colors: {
      primary: '#C1121F',
      secondary: '#780000',
      accent: '#4CAF50',
      background: '#FAFAFA',
      surface: '#FFFFFF',
      text: '#2B2B2B',
      textSecondary: '#5A5A5A',
      border: '#E8E8E8',
      success: '#4CAF50',
      warning: '#FF9800',
      error: '#C1121F',
      info: '#2196F3',
      keypadBackground: '#FAFAFA',
      keypadButton: '#C1121F',
      keypadButtonText: '#FFFFFF',
      backButton: '#6B7280',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(43, 43, 43, 0.5)',
      confirmButton: '#4CAF50',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'small',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'subtle',
    animations: 'subtle',
  },
  {
    id: 'healthy-bio',
    name: 'Healthy / Bio',
    description: 'Fraîche, rassurante et naturelle',
    colors: {
      primary: '#2E7D32',
      secondary: '#1B5E20',
      accent: '#A5D6A7',
      background: '#FFFFFF',
      surface: '#F1F8F4',
      text: '#2F3E2F',
      textSecondary: '#557856',
      border: '#C8E6C9',
      success: '#2E7D32',
      warning: '#FF9800',
      error: '#D32F2F',
      info: '#0288D1',
      keypadBackground: '#FFFFFF',
      keypadButton: '#2E7D32',
      keypadButtonText: '#FFFFFF',
      backButton: '#6B7280',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(47, 62, 47, 0.5)',
      confirmButton: '#2E7D32',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'medium',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'subtle',
    animations: 'smooth',
  },
  {
    id: 'kiosk-pro',
    name: 'Kiosk Ultra Moderne',
    description: 'Style des bornes McDo, KFC, Burger King',
    colors: {
      primary: '#0D6EFD',
      secondary: '#0B5ED7',
      accent: '#6EA8FE',
      background: '#F8F9FA',
      surface: '#FFFFFF',
      text: '#212529',
      textSecondary: '#6C757D',
      border: '#DEE2E6',
      success: '#198754',
      warning: '#FFC107',
      error: '#DC3545',
      info: '#0DCAF0',
      keypadBackground: '#FFFFFF',
      keypadButton: '#0D6EFD',
      keypadButtonText: '#FFFFFF',
      backButton: '#6C757D',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(33, 37, 41, 0.5)',
      confirmButton: '#198754',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'medium',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'medium',
    animations: 'smooth',
  },
  {
    id: 'coffee-shop',
    name: 'Coffee Shop',
    description: 'Ambiance café chaleureuse',
    colors: {
      primary: '#6F4E37',
      secondary: '#8B6F47',
      accent: '#D4A574',
      background: '#FBF8F3',
      surface: '#FFFFFF',
      text: '#3E2723',
      textSecondary: '#5D4037',
      border: '#E8DFD5',
      success: '#689F38',
      warning: '#F57C00',
      error: '#D32F2F',
      info: '#0288D1',
      keypadBackground: '#FFFFFF',
      keypadButton: '#6F4E37',
      keypadButtonText: '#FFFFFF',
      backButton: '#8D6E63',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(62, 39, 35, 0.5)',
      confirmButton: '#689F38',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'large',
    fontFamily: 'Georgia, serif',
    shadows: 'medium',
    animations: 'smooth',
  },
  {
    id: 'pizzeria',
    name: 'Pizzeria Italienne',
    description: 'Couleurs italiennes authentiques',
    colors: {
      primary: '#D32F2F',
      secondary: '#C62828',
      accent: '#EF5350',
      background: '#FFFEF7',
      surface: '#FFFFFF',
      text: '#212121',
      textSecondary: '#616161',
      border: '#E0E0E0',
      success: '#43A047',
      warning: '#FFB300',
      error: '#D32F2F',
      info: '#1976D2',
      keypadBackground: '#FFFEF7',
      keypadButton: '#D32F2F',
      keypadButtonText: '#FFFFFF',
      backButton: '#757575',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(33, 33, 33, 0.5)',
      confirmButton: '#43A047',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'medium',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'medium',
    animations: 'smooth',
  },
  {
    id: 'ice-cream',
    name: 'Glacier / Desserts',
    description: 'Pastel doux et gourmand',
    colors: {
      primary: '#FF6B9D',
      secondary: '#FF8AB8',
      accent: '#FFC2E0',
      background: '#FFF5F9',
      surface: '#FFFFFF',
      text: '#4A1942',
      textSecondary: '#8E4585',
      border: '#FFD4E8',
      success: '#4CAF50',
      warning: '#FF9800',
      error: '#E91E63',
      info: '#00BCD4',
      keypadBackground: '#FFFFFF',
      keypadButton: '#FF6B9D',
      keypadButtonText: '#FFFFFF',
      backButton: '#9C27B0',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(74, 25, 66, 0.5)',
      confirmButton: '#4CAF50',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'full',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'medium',
    animations: 'energetic',
  },
  {
    id: 'steakhouse',
    name: 'Steakhouse Premium',
    description: 'Élégance carnivore raffinée',
    colors: {
      primary: '#8B0000',
      secondary: '#A52A2A',
      accent: '#CD853F',
      background: '#1A1110',
      surface: '#2C1F1E',
      text: '#F5E6D3',
      textSecondary: '#D4B59E',
      border: '#4A3635',
      success: '#689F38',
      warning: '#F57C00',
      error: '#D32F2F',
      info: '#0288D1',
      keypadBackground: '#2C1F1E',
      keypadButton: '#8B0000',
      keypadButtonText: '#F5E6D3',
      backButton: '#5D4037',
      backButtonText: '#F5E6D3',
      modalBackground: '#2C1F1E',
      modalOverlay: 'rgba(0, 0, 0, 0.85)',
      confirmButton: '#8B0000',
      confirmButtonText: '#F5E6D3',
    },
    borderRadius: 'small',
    fontFamily: 'Georgia, serif',
    shadows: 'bold',
    animations: 'subtle',
  },
  {
    id: 'vegan',
    name: 'Vegan / Végétarien',
    description: 'Naturel, éthique et moderne',
    colors: {
      primary: '#7CB342',
      secondary: '#558B2F',
      accent: '#AED581',
      background: '#F9FBF7',
      surface: '#FFFFFF',
      text: '#33691E',
      textSecondary: '#689F38',
      border: '#DCEDC8',
      success: '#7CB342',
      warning: '#FF9800',
      error: '#E53935',
      info: '#26C6DA',
      keypadBackground: '#FFFFFF',
      keypadButton: '#7CB342',
      keypadButtonText: '#FFFFFF',
      backButton: '#78909C',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(51, 105, 30, 0.5)',
      confirmButton: '#7CB342',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'medium',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'subtle',
    animations: 'smooth',
  },
  {
    id: 'bakery',
    name: 'Boulangerie Artisanale',
    description: 'Chaleur et tradition du pain frais',
    colors: {
      primary: '#E65100',
      secondary: '#F57C00',
      accent: '#FFB74D',
      background: '#FFF8E1',
      surface: '#FFFFFF',
      text: '#4E342E',
      textSecondary: '#6D4C41',
      border: '#FFECB3',
      success: '#689F38',
      warning: '#FFA726',
      error: '#E53935',
      info: '#29B6F6',
      keypadBackground: '#FFFFFF',
      keypadButton: '#E65100',
      keypadButtonText: '#FFFFFF',
      backButton: '#8D6E63',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(78, 52, 46, 0.5)',
      confirmButton: '#689F38',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'medium',
    fontFamily: 'Georgia, serif',
    shadows: 'medium',
    animations: 'smooth',
  },
  {
    id: 'seafood',
    name: 'Restaurant Fruits de Mer',
    description: 'Fraîcheur océanique et marine',
    colors: {
      primary: '#0277BD',
      secondary: '#01579B',
      accent: '#4FC3F7',
      background: '#E1F5FE',
      surface: '#FFFFFF',
      text: '#01579B',
      textSecondary: '#0277BD',
      border: '#B3E5FC',
      success: '#00897B',
      warning: '#FF6F00',
      error: '#D32F2F',
      info: '#0288D1',
      keypadBackground: '#FFFFFF',
      keypadButton: '#0277BD',
      keypadButtonText: '#FFFFFF',
      backButton: '#546E7A',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(1, 87, 155, 0.5)',
      confirmButton: '#00897B',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'medium',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'medium',
    animations: 'smooth',
  },
  {
    id: 'mexican',
    name: 'Restaurant Mexicain',
    description: 'Vibrant, festif et coloré',
    colors: {
      primary: '#D84315',
      secondary: '#F4511E',
      accent: '#FFD54F',
      background: '#FFF3E0',
      surface: '#FFFFFF',
      text: '#3E2723',
      textSecondary: '#5D4037',
      border: '#FFE0B2',
      success: '#689F38',
      warning: '#FF6F00',
      error: '#D84315',
      info: '#0288D1',
      keypadBackground: '#FFFFFF',
      keypadButton: '#D84315',
      keypadButtonText: '#FFFFFF',
      backButton: '#795548',
      backButtonText: '#FFFFFF',
      modalBackground: '#FFFFFF',
      modalOverlay: 'rgba(62, 39, 35, 0.5)',
      confirmButton: '#689F38',
      confirmButtonText: '#FFFFFF',
    },
    borderRadius: 'medium',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    shadows: 'bold',
    animations: 'energetic',
  },
];

export const COLOR_PRESETS = {
  orange: { primary: '#F97316', secondary: '#FB923C', accent: '#FDBA74' },
  blue: { primary: '#3B82F6', secondary: '#60A5FA', accent: '#93C5FD' },
  green: { primary: '#10B981', secondary: '#34D399', accent: '#6EE7B7' },
  purple: { primary: '#8B5CF6', secondary: '#A78BFA', accent: '#C4B5FD' },
  pink: { primary: '#EC4899', secondary: '#F472B6', accent: '#F9A8D4' },
  red: { primary: '#EF4444', secondary: '#F87171', accent: '#FCA5A5' },
  yellow: { primary: '#F59E0B', secondary: '#FBBF24', accent: '#FCD34D' },
  teal: { primary: '#14B8A6', secondary: '#2DD4BF', accent: '#5EEAD4' },
  indigo: { primary: '#6366F1', secondary: '#818CF8', accent: '#A5B4FC' },
  cyan: { primary: '#06B6D4', secondary: '#22D3EE', accent: '#67E8F9' },
  coral: { primary: '#FF6B6B', secondary: '#FF8E8E', accent: '#FFB3B3' },
  mint: { primary: '#00D4AA', secondary: '#2EE4B9', accent: '#5DF0CA' },
  lavender: { primary: '#A855F7', secondary: '#C084FC', accent: '#E9D5FF' },
  amber: { primary: '#D97706', secondary: '#F59E0B', accent: '#FBBF24' },
  emerald: { primary: '#059669', secondary: '#10B981', accent: '#34D399' },
  rose: { primary: '#E11D48', secondary: '#F43F5E', accent: '#FB7185' },
  sky: { primary: '#0284C7', secondary: '#0EA5E9', accent: '#38BDF8' },
  lime: { primary: '#65A30D', secondary: '#84CC16', accent: '#A3E635' },
  fuchsia: { primary: '#C026D3', secondary: '#D946EF', accent: '#E879F9' },
  slate: { primary: '#475569', secondary: '#64748B', accent: '#94A3B8' },
  fastfood: { primary: '#E63946', secondary: '#C62828', accent: '#FF9F1C' },
  gold: { primary: '#D4AF37', secondary: '#B8962E', accent: '#E8C468' },
  japanese: { primary: '#C1121F', secondary: '#780000', accent: '#4CAF50' },
  bio: { primary: '#2E7D32', secondary: '#1B5E20', accent: '#A5D6A7' },
  kiosk: { primary: '#0D6EFD', secondary: '#0B5ED7', accent: '#6EA8FE' },
  coffee: { primary: '#6F4E37', secondary: '#8B6F47', accent: '#D4A574' },
  ocean: { primary: '#0891B2', secondary: '#06B6D4', accent: '#22D3EE' },
  forest: { primary: '#15803D', secondary: '#16A34A', accent: '#4ADE80' },
  crimson: { primary: '#DC2626', secondary: '#B91C1C', accent: '#F87171' },
  sunset: { primary: '#F97316', secondary: '#EA580C', accent: '#FDBA74' },
};

export interface ThemeBackground {
  type: 'none' | 'image' | 'video' | 'lottie';
  data?: string;
  opacity?: number;
  blur?: number;
}

export interface ThemeSettings {
  templateId: string;
  customColors?: Partial<ThemeColors>;
  borderRadius?: 'none' | 'small' | 'medium' | 'large' | 'full';
  fontFamily?: string;
  shadows?: 'none' | 'subtle' | 'medium' | 'bold';
  animations?: 'none' | 'subtle' | 'smooth' | 'energetic';
  background?: ThemeBackground;
}

class ThemeService {
  getTemplate(id: string): ThemeTemplate | undefined {
    return THEME_TEMPLATES.find(t => t.id === id);
  }

  getAllTemplates(): ThemeTemplate[] {
    return THEME_TEMPLATES;
  }

  applyTheme(settings: ThemeSettings): void {
    const template = this.getTemplate(settings.templateId) || THEME_TEMPLATES[0];
    const colors = { ...template.colors, ...settings.customColors };
    const borderRadius = settings.borderRadius || template.borderRadius;
    const fontFamily = settings.fontFamily || template.fontFamily;
    const shadows = settings.shadows || template.shadows;
    const animations = settings.animations || template.animations;

    const root = document.documentElement;

    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${this.kebabCase(key)}`, value);
    });

    const borderRadiusValues = {
      none: '0px',
      small: '0.25rem',
      medium: '0.5rem',
      large: '0.75rem',
      full: '1rem',
    };
    root.style.setProperty('--border-radius', borderRadiusValues[borderRadius]);

    root.style.setProperty('--font-family', fontFamily);

    const shadowValues = {
      none: 'none',
      subtle: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      medium: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      bold: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    };
    root.style.setProperty('--shadow', shadowValues[shadows]);

    const animationDurations = {
      none: '0ms',
      subtle: '150ms',
      smooth: '300ms',
      energetic: '500ms',
    };
    root.style.setProperty('--animation-duration', animationDurations[animations]);

    console.log('[ThemeService] Thème appliqué:', {
      template: template.name,
      colors,
      borderRadius,
      fontFamily,
      shadows,
      animations,
    });
  }

  private kebabCase(str: string): string {
    return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
  }

  generateCSSVariables(settings: ThemeSettings): string {
    const template = this.getTemplate(settings.templateId) || THEME_TEMPLATES[0];
    const colors = { ...template.colors, ...settings.customColors };
    const borderRadius = settings.borderRadius || template.borderRadius;
    const fontFamily = settings.fontFamily || template.fontFamily;
    const shadows = settings.shadows || template.shadows;

    const borderRadiusValues = {
      none: '0px',
      small: '0.25rem',
      medium: '0.5rem',
      large: '0.75rem',
      full: '1rem',
    };

    const shadowValues = {
      none: 'none',
      subtle: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      medium: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      bold: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    };

    const cssVars = Object.entries(colors)
      .map(([key, value]) => `  --color-${this.kebabCase(key)}: ${value};`)
      .join('\n');

    return `:root {\n${cssVars}\n  --border-radius: ${borderRadiusValues[borderRadius]};\n  --font-family: ${fontFamily};\n  --shadow: ${shadowValues[shadows]};\n}`;
  }
}

export const themeService = new ThemeService();
